/**
 * useBuddyGroupOps — React Query hooks for buddy group management
 *
 * Endpoints under /api/buddy-group-ops:
 *   POST   /create                  — Create a new buddy group for a rodeo
 *   POST   /join                    — Add member to existing group
 *   POST   /validate                — Pre-flight validation
 *   PUT    /:id/primary             — Set primary cardholder
 *   GET    /rodeo/:rodeoId/my-group — Get current user's group
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const buddyGroupOpsKeys = {
  all: ['buddy-group-ops'] as const,
  myGroup: (rodeoId: string) =>
    [...buddyGroupOpsKeys.all, 'my-group', rodeoId] as const,
  validation: (rodeoId: string) =>
    [...buddyGroupOpsKeys.all, 'validate', rodeoId] as const,
  userGroup: (rodeoId: string, userProfileId: string) =>
    [...buddyGroupOpsKeys.all, 'user-group', rodeoId, userProfileId] as const,
};

// Detailed shape returned by the admin/user buddy-group lookup endpoints
// — richer than the trimmed `BuddyGroup` above. The admin manual-entry
// dialog needs preferences + member list to render a notice and lock
// the preference dropdowns to the group's selections.
export interface BuddyGroupDetail {
  id: string;
  rodeo_id: string;
  group_name: string;
  status: 'pending' | 'active' | 'locked' | 'disbanded';
  primary_member_user_profile_id: string | null;
  preferences_first: string | null;
  preferences_second: string | null;
  preferences_locked_at: string | null;
  members: Array<{
    id: string;
    user_profile_id: string;
    is_primary: boolean;
    display_name: string | null;
    member_number: string | null;
    email: string | null;
  }>;
}

// --- Types ---

export interface BuddyGroupMember {
  id: string;
  user_profile_id: string;
  first_name: string;
  last_name: string;
  is_primary: boolean;
}

export interface BuddyGroup {
  id: string;
  rodeo_id: string;
  group_name: string;
  status: 'pending' | 'active' | 'locked' | 'disbanded';
  primary_member_user_profile_id: string | null;
  members: BuddyGroupMember[];
}

export interface CreateBuddyGroupData {
  rodeo_id: string;
  group_name?: string;
  primary_card_owner_org_id?: string;
  primary_card_owner_user_id?: string;
}

export interface JoinBuddyGroupData {
  buddy_group_id: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
}

export interface ValidateBuddyGroupData {
  rodeo_id: string;
  buddy_group_id?: string;
}

export interface SetPrimaryData {
  groupId: string;
  user_profile_id: string;
}

export interface BuddyGroupValidation {
  valid: boolean;
  buddy_groups_enabled: boolean;
  double_entry_active: boolean;
  member_eligible: boolean;
  existing_group_id: string | null;
  errors: Array<{ code: string; message: string }>;
  settings: {
    max_buddy_group_size: number;
    member_only_buddy_groups: boolean;
    duplicate_detection_mode: string;
  };
}

// --- Query Hooks ---

/**
 * Get the current user's buddy group for a specific rodeo.
 * Returns null if user is not in a buddy group.
 *
 * Backend wraps the payload as `{ buddy_group: { ... } }` (mirrors the
 * service-function path at services/buddy-group.service.ts:getMyGroup),
 * so we unwrap here. A bare `BuddyGroup` cast would leave `status`,
 * `primary_member_user_profile_id`, and friends `undefined`.
 *
 * The endpoint is auth-gated; firing it for an anonymous viewer
 * triggers the http-client's "401 → clear auth state" cascade, which
 * has zero useful effect (anonymous users can't be in a buddy group)
 * but shows up as a spinning page after a seed reset that invalidates
 * existing sessions. Callers pass `isAuthenticated` as a second arg so
 * the hook stays disabled until the viewer actually has a session.
 */
export function useMyBuddyGroup(
  rodeoId: string,
  isAuthenticated: boolean = true,
) {
  return useQuery({
    queryKey: buddyGroupOpsKeys.myGroup(rodeoId),
    queryFn: async (): Promise<BuddyGroup | null> => {
      try {
        const res = await httpClient.get<{ buddy_group: BuddyGroup | null }>(
          `/buddy-group-ops/rodeo/${rodeoId}/my-group`,
        );
        return res?.buddy_group ?? null;
      } catch (err: any) {
        // 400 with no group = user not in a group — not an error
        if (err?.statusCode === 400 || err?.message?.includes('not found')) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!rodeoId && isAuthenticated,
  });
}

/**
 * Admin-only lookup of an arbitrary user's buddy group for a rodeo.
 * Used by the admin "Add Contestant Manually" dialog to detect that the
 * picked contestant is already in a buddy group for the rodeo, so the
 * UI can surface a notice and disable the 1st/2nd preference dropdowns
 * (pre-filled with the group's locked selections).
 *
 * Returns null when the user isn't in any active group for the rodeo.
 *
 * Hits `GET /api/buddy-group-admin-ops/rodeo/:rodeoId/user/:userProfileId/group`
 * — admin/delegate-only on the backend.
 */
export function useUserBuddyGroupForRodeo(
  rodeoId: string | null | undefined,
  userProfileId: string | null | undefined,
) {
  return useQuery({
    queryKey: buddyGroupOpsKeys.userGroup(rodeoId ?? '', userProfileId ?? ''),
    queryFn: async (): Promise<BuddyGroupDetail | null> => {
      if (!rodeoId || !userProfileId) return null;
      try {
        const res = await httpClient.get<{ buddy_group: BuddyGroupDetail | null }>(
          `/buddy-group-admin-ops/rodeo/${rodeoId}/user/${userProfileId}/group`,
        );
        return res?.buddy_group ?? null;
      } catch (err: any) {
        // 400/404 with no group = user not in a group — not an error
        if (
          err?.statusCode === 400 ||
          err?.statusCode === 404 ||
          err?.message?.includes('not found')
        ) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!rodeoId && !!userProfileId,
  });
}

// --- Mutation Hooks ---

/**
 * Create a new buddy group for a rodeo.
 * The current user becomes the first member.
 */
export function useCreateBuddyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBuddyGroupData) =>
      httpClient.post<BuddyGroup>('/buddy-group-ops/create', data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: buddyGroupOpsKeys.myGroup(variables.rodeo_id),
      });
    },
  });
}

/**
 * Join an existing buddy group.
 * Requires name + DOB for duplicate detection.
 */
export function useJoinBuddyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: JoinBuddyGroupData) =>
      httpClient.post<BuddyGroup>('/buddy-group-ops/join', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
    },
  });
}

/**
 * Pre-flight validation for buddy group membership.
 * Checks size limits, membership requirements, duplicates.
 */
export function useValidateBuddyGroup() {
  return useMutation({
    mutationFn: (data: ValidateBuddyGroupData) =>
      httpClient.post<BuddyGroupValidation>('/buddy-group-ops/validate', data),
  });
}

/**
 * Set the primary cardholder for a buddy group.
 * Only the group creator or an admin can do this.
 */
export function useSetPrimaryCardholder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, user_profile_id }: SetPrimaryData) =>
      httpClient.put<BuddyGroup>(
        `/buddy-group-ops/${groupId}/primary`,
        { user_profile_id },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
    },
  });
}
